<template>
  <UiDialog :open="true" title="Preferences" @update:open="value => { if (!value) emit('close'); }">
    <form class="space-y-4" @submit.prevent="submit">
      <UiTabs v-model="activeTab" label="Preference sections" :options="preferenceTabs" />

      <!-- Section: Learning & Bot Settings -->
      <section v-if="activeTab === 'learning'" class="space-y-4" aria-label="Learning preferences">
        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="transcription-pref">
            Transcription Format
          </label>
          <select
            id="transcription-pref"
            :value="selectedTranscription"
            class="ui-input w-full"
            @change="onTranscriptionChange"
          >
            <option
              v-for="t in availableTranscriptions"
              :key="t.code"
              :value="t.code"
            >
              {{ t.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            Pronunciation guide displayed on flashcards (from bot settings).
          </p>
        </div>

        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="translation-pref">
            Translation Language
          </label>
          <select
            id="translation-pref"
            :value="selectedTranslation"
            class="ui-input w-full"
            @change="onTranslationChange"
          >
            <option
              v-for="lang in availableLanguages"
              :key="lang.code"
              :value="lang.code"
            >
              {{ lang.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            Target language for card definitions and translations.
          </p>
        </div>

        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500">
            Active Dictionaries / Decks
          </label>
          <div class="flex flex-wrap gap-2 pt-1">
            <button
              v-for="d in availableDictionaries"
              :key="d.code"
              type="button"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition select-none"
              :class="selectedDicts.includes(d.code)
                ? 'bg-blue-50 border-blue-400 font-semibold text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'"
              @click="toggleDictionary(d.code)"
            >
              <i
                class="fa-solid"
                :class="selectedDicts.includes(d.code) ? 'fa-square-check text-blue-600 dark:text-blue-400' : 'fa-square text-gray-300'"
              ></i>
              <span>{{ d.name }}</span>
            </button>
          </div>
        </div>

        <div class="space-y-3 pt-1 border-t border-gray-100 dark:border-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-bold uppercase text-gray-500 block">Auto-play Pronunciation</span>
              <span class="text-xs text-gray-400">Play native audio when a new card appears</span>
            </div>
            <UiSwitch v-model="autoPlayAudio" label="Auto-play audio" />
          </div>

          <div>
            <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="daily-goal-pref">Daily Study Goal</label>
            <select
              id="daily-goal-pref"
              :value="dailyGoal"
              class="ui-input w-full"
              @change="onDailyGoalChange"
            >
              <option :value="10">10 cards / day (Light)</option>
              <option :value="20">20 cards / day (Recommended)</option>
              <option :value="30">30 cards / day (Intermediate)</option>
              <option :value="50">50 cards / day (Intensive)</option>
            </select>
          </div>
        </div>

        <!-- Quick Access to Word Lists from Bot Info -->
        <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500">
            Word Lists Management
          </label>
          <p class="text-xs text-gray-400 mb-2">
            Inspect words in your current course directly on the learning canvas:
          </p>
          <div class="grid grid-cols-2 gap-2">
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('active')"
            >
              <i class="fa-solid fa-book-open mr-1.5 text-blue-500"></i>
              <span>Active Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('learned')"
            >
              <i class="fa-solid fa-heart mr-1.5 text-rose-500"></i>
              <span>Learned Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('postponed')"
            >
              <i class="fa-solid fa-clock mr-1.5 text-amber-500"></i>
              <span>Postponed Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('part_of_speech')"
            >
              <i class="fa-solid fa-shapes mr-1.5 text-emerald-500"></i>
              <span>By Part of Speech</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs col-span-2"
              @click="jumpToWordList('removed')"
            >
              <i class="fa-solid fa-trash mr-1.5 text-gray-500"></i>
              <span>Removed Words</span>
            </UiButton>
          </div>
        </div>
      </section>

      <!-- Section: Appearance (Themes & Wash - matching TaskManager exactly) -->
      <section v-else-if="activeTab === 'appearance'" class="space-y-4" aria-label="Appearance preferences">
        <div>
          <label class="mb-1.5 block text-xs font-bold uppercase text-gray-500">
            Theme Palette
          </label>
          
          <!-- Dropdown selector -->
          <select
            id="theme-preference"
            :value="selectedTheme"
            class="ui-input w-full mb-3"
            @change="onThemeChange"
          >
            <optgroup v-for="group in themeGroups" :key="group.label" :label="group.label">
              <option v-for="theme in group.options" :key="theme.value" :value="theme.value">
                {{ theme.label }}
              </option>
            </optgroup>
          </select>

          <!-- Interactive theme pills for immediate 1-click theme switching -->
          <div class="space-y-2.5">
            <div v-for="group in themeGroups" :key="group.label" class="space-y-1">
              <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">
                {{ group.label }}
              </span>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                <button
                  v-for="theme in group.options"
                  :key="theme.value"
                  type="button"
                  class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition text-left"
                  :class="selectedTheme === theme.value
                    ? 'border-[var(--ui-primary)] ring-2 ring-[var(--ui-primary)] bg-[var(--ui-selected)] text-[var(--ui-selected-text)] font-semibold shadow-sm'
                    : 'border-[var(--ui-border)] hover:bg-[var(--ui-surface-hover)] text-[var(--ui-text)]'"
                  @click="selectTheme(theme.value)"
                >
                  <span
                    class="w-3 h-3 rounded-full border border-black/10 shrink-0"
                    :style="palettePreviewStyle(theme.value)"
                  ></span>
                  <span class="truncate">{{ theme.label }}</span>
                </button>
              </div>
            </div>
          </div>

          <p class="mt-2 text-xs text-gray-400">
            Applied instantly and persisted for this browser profile.
          </p>
        </div>

        <div v-if="selectedWashGroup === 'light'" class="space-y-2 pt-2 border-t border-[var(--ui-border)]">
          <div class="flex items-baseline justify-between gap-3">
            <label class="text-xs font-bold uppercase text-gray-500" for="light-artwork-wash">
              Light background wash
            </label>
            <output class="text-sm font-medium text-gray-700" for="light-artwork-wash">
              {{ lightWashOpacity }}%
            </output>
          </div>
          <input
            id="light-artwork-wash"
            v-model.number="lightWashOpacity"
            class="appearance-wash-slider w-full"
            type="range"
            min="0"
            max="100"
            step="1"
            aria-describedby="light-artwork-wash-help"
            @input="handleWashChange"
          />
          <p id="light-artwork-wash-help" class="text-xs text-gray-400">
            Mixes palette wash into light artwork. The default is 80%.
          </p>
        </div>

        <div v-else class="space-y-2 pt-2 border-t border-[var(--ui-border)]">
          <div class="flex items-baseline justify-between gap-3">
            <label class="text-xs font-bold uppercase text-gray-500" for="dark-artwork-wash">
              Dark background wash
            </label>
            <output class="text-sm font-medium text-gray-700" for="dark-artwork-wash">
              {{ darkWashOpacity }}%
            </output>
          </div>
          <input
            id="dark-artwork-wash"
            v-model.number="darkWashOpacity"
            class="appearance-wash-slider w-full"
            type="range"
            min="0"
            max="100"
            step="1"
            aria-describedby="dark-artwork-wash-help"
            @input="handleWashChange"
          />
          <p id="dark-artwork-wash-help" class="text-xs text-gray-400">
            Mixes black into dark artwork. The default is 60%.
          </p>
        </div>
      </section>

      <!-- Section: Telegram Connection -->
      <section v-else-if="activeTab === 'telegram'" class="space-y-4" aria-label="Telegram preferences">
        <div v-if="currentUser" class="p-4 rounded-xl border border-gray-200 bg-white/50 space-y-3">
          <div class="flex items-center gap-3">
            <UiAvatar :name="displayName" size="lg" />
            <div>
              <p class="font-bold text-sm text-gray-900">{{ displayName }}</p>
              <p v-if="currentUser.username" class="text-xs text-gray-500">@{{ currentUser.username }}</p>
              <p class="text-[11px] text-gray-400">Telegram ID: {{ currentUser.user_id || currentUser.id }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2 pt-1">
            <span class="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
            <span class="text-xs text-emerald-700 font-medium">Native Telegram OAuth Active</span>
          </div>
          <div class="flex flex-wrap gap-2 pt-2">
            <UiButton variant="secondary" size="sm" type="button" @click="handleReLogin">
              <i class="fa-solid fa-arrows-rotate mr-1.5"></i>
              Switch / Re-login
            </UiButton>
            <UiButton
              variant="ghost"
              size="sm"
              type="button"
              class="text-blue-600 hover:text-blue-700"
              @click="openTelegramBot"
            >
              <i class="fa-brands fa-telegram mr-1.5"></i>
              Open Bot in Telegram
            </UiButton>
          </div>
        </div>
        <div v-else class="text-center p-6 rounded-xl border border-dashed space-y-3">
          <p class="text-sm text-gray-500">You are currently studying as a guest.</p>
          <UiButton variant="primary" size="md" shape="pill" type="button" @click="handleReLogin">
            <i class="fa-brands fa-telegram mr-1.5"></i>
            Login with Telegram
          </UiButton>
        </div>
      </section>

      <!-- Section: Storage & Cache (adapted from TaskManager) -->
      <section v-else class="space-y-4" aria-label="Storage preferences">
        <div>
          <h4 class="text-xs font-bold uppercase text-gray-500">Offline Storage & Audio Cache</h4>
          <p class="mt-1 text-xs text-gray-400">
            Downloaded audio pronunciations and flashcard data are cached in IndexedDB for instant, offline playback.
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 bg-white/50 p-4 space-y-2 text-xs">
          <div class="flex justify-between text-gray-700">
            <span>Offline Vocabulary Cached:</span>
            <span class="font-medium text-gray-900">{{ localSync.state.totalWords }} words</span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Pending 3-Way Merge Changes:</span>
            <span class="font-medium text-gray-900">{{ localSync.state.pending }} changes</span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Cached Audio Recordings:</span>
            <span class="font-medium text-gray-900">{{ audioCacheCount }} files</span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Offline Sync Storage:</span>
            <span class="font-medium text-gray-900">Active (IndexedDB)</span>
          </div>
        </div>


        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="cache-limit-pref">
            Audio Cache Limit
          </label>
          <select
            id="cache-limit-pref"
            :value="cacheLimitChoice"
            class="ui-input w-full"
            @change="onCacheLimitChange"
          >
            <option value="automatic">Automatic (System default)</option>
            <option value="104857600">100 MB</option>
            <option value="262144000">250 MB</option>
            <option value="524288000">500 MB</option>
            <option value="1073741824">1 GB</option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            High audio cache limits ensure full pronunciation decks remain playable with no network lag.
          </p>
        </div>

        <div class="pt-2">
          <UiButton
            variant="ghost"
            type="button"
            class="user-menu-action text-xs text-amber-700 hover:text-amber-800"
            :disabled="isClearingCache"
            @click="cleanCacheAndRefresh"
          >
            <i class="fa-solid fa-rotate mr-1.5" :class="{ 'animate-spin': isClearingCache }"></i>
            <span>{{ isClearingCache ? 'Clearing cache...' : 'Clean Cache and Refresh Application' }}</span>
          </UiButton>
        </div>
      </section>

      <p v-if="error" class="text-sm text-red-500">{{ error }}</p>

      <div class="flex justify-end gap-2 pt-3 border-t border-[var(--ui-border)]">
        <UiButton variant="secondary" type="button" @click="emit('close')">Cancel</UiButton>
        <UiButton
          variant="primary"
          type="submit"
          :disabled="loading"
          class="disabled:opacity-50"
        >
          <span v-if="loading"><i class="fa-solid fa-circle-notch mr-1.5 animate-spin"></i></span>
          Save Preferences
        </UiButton>
      </div>
    </form>
  </UiDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  UiButton,
  UiDialog,
  UiSwitch,
  UiTabs,
  UiAvatar,
} from '../ui';
import {
  activeTheme,
  artworkWashGroupForTheme,
  darkArtworkWashOpacity,
  lightArtworkWashOpacity,
  setArtworkWashOpacity,
  setTheme,
  themeOptions,
  type AppTheme,
} from '../composables/useTheme';
import { useAuth } from '../composables/useAuth';
import { api } from '../services/api';
import { mediaCache } from '../services/mediaCacheService';
import { localSync } from '../services/LocalSyncService';


const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'filter-action', filterKind: string): void;
  (e: 'open-login'): void;
  (e: 'preferences-saved'): void;
}>();

type PreferenceTab = 'learning' | 'appearance' | 'telegram' | 'storage';
const activeTab = ref<PreferenceTab>('learning');

const preferenceTabs = [
  { value: 'learning', label: 'Learning' },
  { value: 'appearance', label: 'Appearance' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'storage', label: 'Storage' },
];

const { currentUser } = useAuth();
const displayName = computed(() => {
  return currentUser.value?.first_name || currentUser.value?.username || 'Guest';
});

// Learning preferences state
const selectedTranscription = ref('ipa');
const selectedTranslation = ref('en');
const selectedDicts = ref<string[]>(['es']);
const autoPlayAudio = ref(true);
const dailyGoal = ref(20);
const botUsername = ref('IgorLearnWordsDevBot');

const availableLanguages = ref([
  { code: 'en', name: '🇺🇸 English' },
  { code: 'ru', name: '🇷🇺 Russian' },
  { code: 'es', name: '🇪🇸 Spanish' },
  { code: 'de', name: '🇩🇪 German' },
  { code: 'fr', name: '🇫🇷 French' },
  { code: 'ka', name: '🇬🇪 Georgian' },
  { code: 'cat', name: 'Catalan' },
]);

const availableTranscriptions = ref([
  { code: 'ipa', name: '🗣️ IPA Broad (Standard)' },
  { code: 'ipa_narrow', name: '🗣️ IPA Narrow (Detailed)' },
  { code: 'lat', name: '🔤 Latin Transliteration' },
  { code: 'cyr', name: '🔤 Cyrillic Transliteration' },
  { code: '-', name: '🙅 No Transcription' },
]);

const availableDictionaries = ref([
  { id: 'es', code: 'es', name: 'Spanish Core' },
  { id: 'ka', code: 'ka', name: 'Georgian Basic' },
  { id: 'cat', code: 'cat', name: 'Catalan' },
]);

// Appearance preferences state
const selectedTheme = ref<AppTheme>(activeTheme.value);
const lightWashOpacity = ref(lightArtworkWashOpacity.value);
const darkWashOpacity = ref(darkArtworkWashOpacity.value);
const selectedWashGroup = computed(() => artworkWashGroupForTheme(selectedTheme.value));

const themeGroups = [
  { label: 'Base', options: themeOptions.filter((t) => t.group === 'Base') },
  { label: 'Light palettes', options: themeOptions.filter((t) => t.group === 'Light palettes') },
  { label: 'Dark palettes', options: themeOptions.filter((t) => t.group === 'Dark palettes') },
];

const palettePreviewStyle = (theme: AppTheme) => {
  const map: Record<AppTheme, string> = {
    light: 'background: #ffffff; border-color: #cbd5e1;',
    dark: 'background: #111827;',
    nord: 'background: #2563eb;',
    matcha: 'background: #3f6f4b;',
    terracotta: 'background: #b45331;',
    orchid: 'background: #7c3aed;',
    aurora: 'background: #5eead4;',
    espresso: 'background: #fbbf24;',
    burgundy: 'background: #fb7185;',
    'neon-noir': 'background: #67e8f9;',
  };
  return map[theme] || 'background: #3b82f6;';
};

const selectTheme = (theme: AppTheme) => {
  selectedTheme.value = theme;
  setTheme(theme, currentUser.value?.user_id);
};

const onThemeChange = (e: Event) => {
  const val = (e.target as HTMLSelectElement).value as AppTheme;
  selectTheme(val);
};

const onTranscriptionChange = (e: Event) => {
  selectedTranscription.value = (e.target as HTMLSelectElement).value;
};

const onTranslationChange = (e: Event) => {
  selectedTranslation.value = (e.target as HTMLSelectElement).value;
};

const onDailyGoalChange = (e: Event) => {
  dailyGoal.value = Number((e.target as HTMLSelectElement).value);
};

const onCacheLimitChange = (e: Event) => {
  cacheLimitChoice.value = (e.target as HTMLSelectElement).value;
};

// Live theme updating on selection
watch(selectedTheme, (newTheme) => {
  setTheme(newTheme, currentUser.value?.user_id);
});

const handleWashChange = () => {
  if (selectedWashGroup.value === 'light') {
    setArtworkWashOpacity('light', lightWashOpacity.value, currentUser.value?.user_id);
  } else {
    setArtworkWashOpacity('dark', darkWashOpacity.value, currentUser.value?.user_id);
  }
};

// Storage state
const audioCacheCount = ref(0);
const cacheLimitChoice = ref('automatic');
const isClearingCache = ref(false);

const loading = ref(false);
const error = ref('');

const toggleDictionary = (code: string) => {
  if (selectedDicts.value.includes(code)) {
    if (selectedDicts.value.length > 1) {
      selectedDicts.value = selectedDicts.value.filter((c) => c !== code);
    }
  } else {
    selectedDicts.value.push(code);
  }
};

const jumpToWordList = (filterKind: string) => {
  emit('filter-action', filterKind);
  emit('close');
};

const handleReLogin = () => {
  emit('close');
  emit('open-login');
};

const openTelegramBot = () => {
  const username = botUsername.value || 'IgorLearnWordsDevBot';
  window.open(`https://t.me/${username}`, '_blank');
};

const cleanCacheAndRefresh = async () => {
  isClearingCache.value = true;
  try {
    await mediaCache.clearCache();
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (err) {
    console.warn('Cache clear warning:', err);
  } finally {
    isClearingCache.value = false;
    window.location.reload();
  }
};

onMounted(async () => {
  try {
    // 1. Load offline IndexedDB preferences & catalogs first
    const [localPrefs, localCatalogs] = await Promise.all([
      localSync.getPreferences(),
      localSync.getCatalogs(),
    ]);
    if (localPrefs) {
      selectedTranscription.value = localPrefs.transcription || 'ipa';
      selectedTranslation.value = localPrefs.translation || 'en';
      if (localPrefs.selected_dictionaries?.length) {
        selectedDicts.value = [...localPrefs.selected_dictionaries];
      }
      autoPlayAudio.value = localPrefs.auto_play_audio ?? true;
      dailyGoal.value = localPrefs.daily_goal || 20;
    }
    if (localCatalogs) {
      if (localCatalogs.bot_username) botUsername.value = localCatalogs.bot_username;
      if (localCatalogs.available_languages?.length) availableLanguages.value = localCatalogs.available_languages;
      if (localCatalogs.available_transcriptions?.length) availableTranscriptions.value = localCatalogs.available_transcriptions;
      if (localCatalogs.available_dictionaries?.length) availableDictionaries.value = localCatalogs.available_dictionaries;
    }

    // 2. If online, also refresh from server endpoint
    if (localSync.state.connection !== 'offline' && typeof navigator !== 'undefined' && navigator.onLine) {
      const prefs = await api.getUserPreferences();
      if (prefs) {
        selectedTranscription.value = prefs.transcription || selectedTranscription.value;
        selectedTranslation.value = prefs.translation || selectedTranslation.value;
        if (prefs.selected_dictionaries?.length) {
          selectedDicts.value = prefs.selected_dictionaries;
        }
        autoPlayAudio.value = prefs.auto_play_audio ?? autoPlayAudio.value;
        dailyGoal.value = prefs.daily_goal || dailyGoal.value;
        if ((prefs as any).bot_username) {
          botUsername.value = (prefs as any).bot_username;
        }
        if (prefs.available_languages?.length) {
          availableLanguages.value = prefs.available_languages;
        }
        if (prefs.available_transcriptions?.length) {
          availableTranscriptions.value = prefs.available_transcriptions;
        }
        if (prefs.available_dictionaries?.length) {
          availableDictionaries.value = prefs.available_dictionaries;
        }
      }
    }
  } catch {
    // Offline or guest: local IndexedDB preferences already applied
  }

  try {
    audioCacheCount.value = await mediaCache.getCachedAudioCount();
  } catch {
    audioCacheCount.value = 0;
  }
});

async function submit() {
  error.value = '';
  loading.value = true;
  try {
    // 1. Save theme preferences locally
    setTheme(selectedTheme.value, currentUser.value?.user_id);
    setArtworkWashOpacity('light', lightWashOpacity.value, currentUser.value?.user_id);
    setArtworkWashOpacity('dark', darkWashOpacity.value, currentUser.value?.user_id);

    // 2. Save learning preferences to IndexedDB & queue 3-way merge mutation
    await localSync.savePreferences({
      transcription: selectedTranscription.value,
      translation: selectedTranslation.value,
      selected_dictionaries: [...selectedDicts.value],
      last_language: selectedDicts.value[0] || 'ka',
      auto_play_audio: autoPlayAudio.value,
      daily_goal: dailyGoal.value,
    });

    // 3. When online, also call direct preferences endpoint for immediate server/mock sync
    if (localSync.state.connection !== 'offline' && typeof navigator !== 'undefined' && navigator.onLine) {
      await api
        .updateUserPreferences({
          transcription: selectedTranscription.value,
          translation: selectedTranslation.value,
          selected_dictionaries: selectedDicts.value,
          auto_play_audio: autoPlayAudio.value,
          daily_goal: dailyGoal.value,
        })
        .catch(() => null);
    }

    emit('preferences-saved');
    emit('close');
  } catch (err: any) {
    error.value = err?.message || 'Failed to save preferences';
  } finally {
    loading.value = false;
  }
}
</script>


<style scoped>
.appearance-wash-slider {
  accent-color: var(--ui-primary);
}
</style>
